import React, { useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import DatePicker from "react-datepicker";
import { Calendar, Clock } from "lucide-react";
import { FaSpinner, FaExclamationTriangle } from "react-icons/fa";
import { formatSlotTimeDisplay, formatDateDDMMYYYY, SLOT_PERIODS, groupSlotsByPeriod } from "../HVTCommonHelpers";

const SlotSection = ({
    primaryBookingId,
    slots,
    slotsMessage,
    isLoadingSlots,
    onSelectSlot,
}) => {
    const { control, register, watch, formState: { errors } } = useFormContext();
    const visitDateRef = useRef(null);
    const visitDate = watch("visitDate");
    const selectedSlot = watch("selectedSlot");
    const tests = watch("tests") || [];
    const hasTests = tests.length > 0;
    const slotGroups = groupSlotsByPeriod(slots);

    const [activeSlotPeriod, setActiveSlotPeriod] = useState("Morning");

    useEffect(() => {
        const groups = groupSlotsByPeriod(slots);
        const firstNonEmpty = SLOT_PERIODS.find((p) => groups[p].length > 0);
        setActiveSlotPeriod(firstNonEmpty || "Morning");
    }, [slots]);

    return (
        <>
            <div className="hvt-quick-section" style={primaryBookingId ? { display: "none" } : undefined}>
                <div className="hvt-quick-section-title required"><Clock size={13} /> Pickup Slot</div>

                <div className="hvt-field mb-2" style={{ maxWidth: 200 }}>
                    <label className="required">Slot Date</label>
                    <div className="position-relative">
                        <Controller
                            name="visitDate"
                            control={control}
                            rules={{ required: "Required" }}
                            render={({ field }) => (
                                <DatePicker
                                    ref={visitDateRef}
                                    portalId="hvt-datepicker-portal"
                                    className="form-control"
                                    selected={field.value}
                                    onChange={field.onChange}
                                    dateFormat="dd/MM/yyyy"
                                    minDate={new Date()}
                                    maxDate={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)}
                                    placeholderText="DD/MM/YYYY"
                                />
                            )}
                        />
                        <Calendar
                            size={16}
                            style={{ position: "absolute", right: 12, top: 10, cursor: "pointer" }}
                            onClick={() => visitDateRef.current?.setFocus()}
                        />
                    </div>
                    {errors.visitDate && <small className="text-danger">{errors.visitDate.message}</small>}
                </div>

                {!hasTests && (
                    <small className="text-muted">Add at least one test to view available pickup slots.</small>
                )}
                {hasTests && isLoadingSlots && (
                    <div className="text-center py-3">
                        <FaSpinner className="fa-spin" color="green" size={22} />
                    </div>
                )}
                {hasTests && !isLoadingSlots && slots.length === 0 && (
                    slotsMessage ? (
                        <div className="alert alert-warning py-2 px-3 mb-0 d-flex align-items-center gap-2" style={{ fontSize: "13px" }}>
                            <FaExclamationTriangle /> {slotsMessage} Try a different date.
                        </div>
                    ) : (
                        <small className="text-muted">Select a pincode/location to see available slots.</small>
                    )
                )}
                {hasTests && slots.length > 0 && (
                    <>
                        <div className="hvt-slot-tabs">
                            {SLOT_PERIODS.filter((period) => slotGroups[period].length > 0).map((period) => (
                                <button
                                    key={period}
                                    type="button"
                                    className={`hvt-slot-tab ${activeSlotPeriod === period ? "active" : ""}`}
                                    onClick={() => setActiveSlotPeriod(period)}
                                >
                                    {period}
                                </button>
                            ))}
                        </div>
                        <div className="hvt-slot-card-grid">
                            {(slotGroups[activeSlotPeriod] || []).map((slot, idx) => {
                                const isAvailable = slot.Available === "A";
                                const isSelected = selectedSlot?.Slot_TIME === slot.Slot_TIME;
                                const [timeMain, timeMer] = formatSlotTimeDisplay(slot.Slot_TIME).split(" ");
                                return (
                                    <button
                                        key={idx}
                                        type="button"
                                        className={`hvt-slot-card ${isSelected ? "hvt-slot-selected" : ""}`}
                                        disabled={!isAvailable}
                                        onClick={() => onSelectSlot(slot)}
                                    >
                                        <div className="hvt-slot-card-time">{timeMain}</div>
                                        <div className="hvt-slot-card-mer">{timeMer}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
                <input type="hidden" {...register("selectedSlot", { validate: (v) => !!v || "Please select a pickup slot" })} />
                {errors.selectedSlot && <small className="text-danger d-block mt-1">{errors.selectedSlot.message}</small>}
            </div>

            {primaryBookingId && (
                <div className="hvt-quick-section">
                    <div className="hvt-quick-section-title"><Clock size={13} /> Visit Slot</div>
                    <div className="hvt-slot-highlight">
                        <div className="hvt-slot-highlight-badge">
                            <Calendar size={18} />
                        </div>
                        <div>
                            <div className="hvt-slot-highlight-main">
                                {formatDateDDMMYYYY(visitDate)}{selectedSlot ? `, ${formatSlotTimeDisplay(selectedSlot.Slot_TIME)}` : ""}
                            </div>
                            <div className="hvt-slot-highlight-sub">Same visit slot as the primary booking</div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SlotSection;
